import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import type { Filters } from '../models/Filters';

/**
 * Hook dedicado a la gestión de filtros de la lista CRM.
 * Separa la responsabilidad de filtrado de useCRMMain (datos, sorting, columnas).
 */
export const useCRMFilters = () => {
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    team_ids: { type: 'string', default: '' as string },
    phone_search: { type: 'string', default: '' as string },
    status_id: { type: 'string', default: undefined as 'null' | undefined },
    shop_ids: { type: 'string', default: '' as string },
    tag_id: { type: 'number', default: undefined as number | undefined },
    shipping_country_codes: { type: 'string', default: '' as string, key: 'shipping_country_codes[]' },
    payment_method_ids: { type: 'string', default: '' as string },
    product_ids: { type: 'string', default: '' as string },
    date_from: { type: 'string', default: '' as string },
    date_to: { type: 'string', default: '' as string },
  });

  // Estado local de filtros
  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [teamIds, setTeamIds] = useState<string[]>(() => {
    if (queryParams.team_ids) {
      return queryParams.team_ids.split(',').filter(Boolean);
    }
    return [];
  });
  const [phoneSearchTerm, setPhoneSearchTerm] = useState<string>((queryParams.phone_search ?? '').trim());
  const [statusId, setStatusId] = useState<number | undefined | null>(() => {
    // Convertir desde el formato string del URL: 'null' -> null, undefined -> undefined, numero -> number
    const rawStatus = queryParams.status_id;
    if (rawStatus === 'null') return null;
    if (rawStatus === undefined) return undefined;
    return Number(rawStatus);
  });
  const [shopIds, setShopIds] = useState<string[]>(() => {
    if (queryParams.shop_ids) {
      return queryParams.shop_ids.split(',').filter(Boolean);
    }
    return [];
  });
  const [tagId, setTagId] = useState<number | undefined>(queryParams.tag_id);
  const [shippingCountryCodes, setShippingCountryCodes] = useState<string[]>(() => {
    if (queryParams.shipping_country_codes) {
      return queryParams.shipping_country_codes.split(',').filter(Boolean);
    }
    return [];
  });
  const [paymentMethodIds, setPaymentMethodIds] = useState<string[]>(() => {
    if (queryParams.payment_method_ids) {
      return queryParams.payment_method_ids.split(',').filter(Boolean);
    }
    return [];
  });
  const [productIds, setProductIds] = useState<string[]>(() => {
    if (queryParams.product_ids) {
      return queryParams.product_ids.split(',').filter(Boolean);
    }
    return [];
  });
  const [dateFrom, setDateFrom] = useState<string>(queryParams.date_from ?? '');
  const [dateTo, setDateTo] = useState<string>(queryParams.date_to ?? '');

  // Sincronización con cambios en la URL
  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    if (queryParams.team_ids) {
      setTeamIds(queryParams.team_ids.split(',').filter(Boolean));
    } else {
      setTeamIds([]);
    }
  }, [queryParams.team_ids]);

  useEffect(() => {
    const paramPhone = (queryParams.phone_search ?? '').trim();
    setPhoneSearchTerm((prev) => (prev === paramPhone ? prev : paramPhone));
  }, [queryParams.phone_search]);

  useEffect(() => {
    // Convertir desde el formato string del URL
    const rawStatus = queryParams.status_id;
    let newStatusId: number | undefined | null;
    if (rawStatus === 'null') {
      newStatusId = null;
    } else if (rawStatus === undefined) {
      newStatusId = undefined;
    } else {
      newStatusId = Number(rawStatus);
    }
    setStatusId((prev) => (prev === newStatusId ? prev : newStatusId));
  }, [queryParams.status_id]);

  useEffect(() => {
    if (queryParams.shop_ids) {
      setShopIds(queryParams.shop_ids.split(',').filter(Boolean));
    } else {
      setShopIds([]);
    }
  }, [queryParams.shop_ids]);

  useEffect(() => {
    setTagId(queryParams.tag_id);
  }, [queryParams.tag_id]);

  useEffect(() => {
    if (queryParams.shipping_country_codes) {
      setShippingCountryCodes(queryParams.shipping_country_codes.split(',').filter(Boolean));
    } else {
      setShippingCountryCodes([]);
    }
  }, [queryParams.shipping_country_codes]);

  useEffect(() => {
    if (queryParams.payment_method_ids) {
      setPaymentMethodIds(queryParams.payment_method_ids.split(',').filter(Boolean));
    } else {
      setPaymentMethodIds([]);
    }
  }, [queryParams.payment_method_ids]);

  useEffect(() => {
    if (queryParams.product_ids) {
      setProductIds(queryParams.product_ids.split(',').filter(Boolean));
    } else {
      setProductIds([]);
    }
  }, [queryParams.product_ids]);

  useEffect(() => {
    setDateFrom(queryParams.date_from ?? '');
  }, [queryParams.date_from]);

  useEffect(() => {
    setDateTo(queryParams.date_to ?? '');
  }, [queryParams.date_to]);

  // Parámetros de filtro para la API (sin sort — lo añade useCRMMain).
  // El filtro de cliente se envía como team_id, mientras que teléfono se combina con la búsqueda general.
  const filterParams = useMemo((): Partial<Filters> => {
    const params: Partial<Filters> = {};
    const combinedSearch = [searchTerm, phoneSearchTerm]
      .map((s) => s?.trim())
      .filter(Boolean)
      .join(' ');
    if (combinedSearch) params.search = combinedSearch;
    if (teamIds.length > 0) params.team_ids = teamIds.join(',');
    // Enviar crm_status_id tanto para valores numéricos como null (sin estado)
    if (statusId !== undefined) params.crm_status_id = statusId;
    if (shopIds.length > 0) params.shop_ids = shopIds.join(',');
    if (tagId) params.tag_id = tagId;
    if (shippingCountryCodes.length > 0) params.shipping_country_codes = shippingCountryCodes.join(',');
    if (paymentMethodIds.length > 0) params.payment_method_ids = paymentMethodIds.join(',');
    if (productIds.length > 0) params.product_ids = productIds.join(',');
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [
    searchTerm,
    teamIds,
    phoneSearchTerm,
    statusId,
    shopIds,
    tagId,
    shippingCountryCodes,
    paymentMethodIds,
    productIds,
    dateFrom,
    dateTo,
  ]);

  // Detectar si hay filtros activos
  const hasActiveFilters = useMemo(
    () =>
      Boolean(searchTerm.trim()) ||
      teamIds.length > 0 ||
      Boolean(phoneSearchTerm.trim()) ||
      statusId !== undefined ||
      shopIds.length > 0 ||
      tagId !== undefined ||
      shippingCountryCodes.length > 0 ||
      paymentMethodIds.length > 0 ||
      productIds.length > 0 ||
      dateFrom !== '' ||
      dateTo !== '',
    [
      searchTerm,
      teamIds,
      phoneSearchTerm,
      statusId,
    shopIds,
      tagId,
      shippingCountryCodes,
      paymentMethodIds,
      productIds,
      dateFrom,
      dateTo,
    ],
  );

  // Handlers
  const handleSearch = useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      setSearchTerm(trimmedTerm);
      updateParam('search', trimmedTerm);
    },
    [updateParam],
  );

  const handleStatusChange = useCallback(
    (id: number | undefined | null) => {
      setStatusId(id);
      // Convertir al formato string del URL: null -> 'null', undefined -> undefined, number -> string
      const urlValue = id === null ? 'null' : id === undefined ? undefined : String(id);
      updateParam('status_id', urlValue as any);
    },
    [updateParam],
  );

  const handleShopIdsChange = useCallback(
    (ids: string[]) => {
      setShopIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('shop_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handleTagChange = useCallback(
    (id: number | undefined) => {
      setTagId(id);
      updateParam('tag_id', id);
    },
    [updateParam],
  );

  const handleShippingCountryCodesChange = useCallback(
    (codes: string[]) => {
      setShippingCountryCodes(codes);
      const codesString = codes.length > 0 ? codes.join(',') : '';
      updateParam('shipping_country_codes', codesString || undefined);
    },
    [updateParam],
  );

  const handlePaymentMethodChange = useCallback(
    (ids: string[]) => {
      setPaymentMethodIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('payment_method_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handleProductIdsChange = useCallback(
    (ids: string[]) => {
      setProductIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('product_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handleTeamIdsChange = useCallback(
    (ids: string[]) => {
      setTeamIds(ids);
      const idsString = ids.length > 0 ? ids.join(',') : '';
      updateParam('team_ids', idsString || undefined);
    },
    [updateParam],
  );

  const handlePhoneSearchChange = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setPhoneSearchTerm(trimmed);
      updateParam('phone_search', trimmed || undefined);
    },
    [updateParam],
  );

  const handleDateRangeChange = useCallback(
    (from: string, to: string) => {
      setDateFrom(from);
      setDateTo(to);
      updateParams({ date_from: from, date_to: to });
    },
    [updateParams],
  );

  // Limpia filtros de URL (mantiene búsqueda general y sorting intactos)
  const handleClearFilters = useCallback(() => {
    updateParams({
      team_ids: '',
      phone_search: '',
      status_id: undefined,
      shop_ids: '',
      tag_id: undefined,
      shipping_country_codes: '',
      payment_method_ids: '',
      product_ids: '',
      date_from: '',
      date_to: '',
    });
  }, [updateParams]);

  return {
    searchTerm,
    teamIds,
    phoneSearchTerm,
    statusId,
    shopIds,
    tagId,
    shippingCountryCodes,
    paymentMethodIds,
    productIds,
    dateFrom,
    dateTo,
    filterParams,
    hasActiveFilters,
    handleSearch,
    handleTeamIdsChange,
    handlePhoneSearchChange,
    handleStatusChange,
    handleShopIdsChange,
    handleTagChange,
    handleShippingCountryCodesChange,
    handlePaymentMethodChange,
    handleProductIdsChange,
    handleDateRangeChange,
    handleClearFilters,
  };
};
